import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ≈ 60s — long enough for any 3DS dance to settle

/**
 * Phase E3 — Stripe Checkout return / status page.
 *
 * Flow:
 *   1) Stripe redirects the user here with ?session_id=cs_…
 *   2) We POLL /api/billing/checkout/status/{session_id} every 2s
 *      until payment_status == 'paid' (or status == 'expired', or we
 *      hit MAX_ATTEMPTS).
 *   3) On 'paid' → cheerful success card + CTA back to Billing.
 *      On 'expired' / failure → clear error + retry CTA.
 *   4) Webhook also writes the same status server-side, so we are
 *      idempotent regardless of which path lands first.
 */
export default function StripeReturnPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | timeout | error
  const [errMsg, setErrMsg] = useState("");
  const attempts = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setErrMsg(t("billingReturn.missingSession", "Missing session_id in the URL."));
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        const ps = r.data?.payment_status;
        const cs = r.data?.status;
        if (ps === "paid") {
          setState("paid");
          return;
        }
        if (cs === "expired") {
          setState("expired");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        timer.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setState("error");
        setErrMsg(e?.response?.data?.detail || String(e));
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [sessionId, t]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="stripe-return-card">
        <CardContent className="p-6 text-center space-y-4">
          {state === "polling" && (
            <>
              <Loader2 className="h-10 w-10 mx-auto animate-spin text-[hsl(var(--accent))]" />
              <h2 className="text-lg font-semibold">
                {t("billingReturn.pollingTitle", "Confirming your payment…")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(
                  "billingReturn.pollingBody",
                  "Stripe is finalizing the charge. This usually takes a few seconds."
                )}
              </p>
              <p className="text-[11px] text-muted-foreground font-mono">
                {sessionId?.slice(0, 24)}…
              </p>
            </>
          )}

          {state === "paid" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-[hsl(var(--ok))]" />
              <h2 className="text-lg font-semibold">
                {t("billingReturn.paidTitle", "You're subscribed")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(
                  "billingReturn.paidBody",
                  "Your plan is now active. Welcome aboard."
                )}
              </p>
              <Button
                onClick={() => navigate("/billing")}
                data-testid="stripe-return-continue"
                className="w-full"
              >
                {t("billingReturn.continue", "Back to Billing")}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </>
          )}

          {state === "expired" && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-[hsl(var(--danger))]" />
              <h2 className="text-lg font-semibold">
                {t("billingReturn.expiredTitle", "Checkout expired")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(
                  "billingReturn.expiredBody",
                  "The Stripe session timed out before payment was completed. You can try again anytime."
                )}
              </p>
              <Button
                onClick={() => navigate("/billing")}
                variant="outline"
                className="w-full"
              >
                {t("billingReturn.backToBilling", "Back to Billing")}
              </Button>
            </>
          )}

          {state === "timeout" && (
            <>
              <AlertTriangle className="h-12 w-12 mx-auto text-[hsl(var(--warn))]" />
              <h2 className="text-lg font-semibold">
                {t("billingReturn.timeoutTitle", "Still processing")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(
                  "billingReturn.timeoutBody",
                  "We couldn't confirm the payment within 60 seconds. Stripe will email you a receipt — refresh Billing in a few minutes."
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.location.reload()}
                >
                  {t("billingReturn.tryAgain", "Refresh")}
                </Button>
                <Button asChild className="flex-1">
                  <Link to="/billing">
                    {t("billingReturn.backToBilling", "Back to Billing")}
                  </Link>
                </Button>
              </div>
            </>
          )}

          {state === "error" && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-[hsl(var(--danger))]" />
              <h2 className="text-lg font-semibold">
                {t("billingReturn.errorTitle", "Something went wrong")}
              </h2>
              <p className="text-sm text-muted-foreground break-words">
                {errMsg ||
                  t(
                    "billingReturn.errorBody",
                    "We couldn't reach Stripe. Please try again or contact support."
                  )}
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/billing">
                  {t("billingReturn.backToBilling", "Back to Billing")}
                </Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
